import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import type { UserInfo } from "../../shared/model";
import { api } from "../sync/api";

interface SessionValue {
  /** undefined while checking, null when signed out */
  user: UserInfo | null | undefined;
  offline: boolean;
  setUser: (u: UserInfo | null) => void;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
}

const Ctx = createContext<SessionValue | null>(null);
const CACHE = "sepia.user";

export function SessionProvider({ children }: { children: ReactNode }) {
  const [user, setUserState] = useState<UserInfo | null | undefined>(undefined);
  const [offline, setOffline] = useState(false);

  const setUser = useCallback((u: UserInfo | null) => {
    setUserState(u);
    try {
      if (u) localStorage.setItem(CACHE, JSON.stringify(u));
      else localStorage.removeItem(CACHE);
    } catch {
      /* private mode */
    }
  }, []);

  const refresh = useCallback(async () => {
    try {
      const r = await api.me();
      setOffline(false);
      setUser(r.user);
    } catch {
      // offline: trust the last known account so cached boards still open
      setOffline(true);
      try {
        const cached = JSON.parse(localStorage.getItem(CACHE) || "null");
        setUserState(cached);
      } catch {
        setUserState(null);
      }
    }
  }, [setUser]);

  const signOut = useCallback(async () => {
    try {
      await api.logout();
    } finally {
      setUser(null);
    }
  }, [setUser]);

  useEffect(() => {
    refresh();
    const on = () => refresh();
    window.addEventListener("online", on);
    return () => window.removeEventListener("online", on);
  }, [refresh]);

  return <Ctx.Provider value={{ user, offline, setUser, refresh, signOut }}>{children}</Ctx.Provider>;
}

export function useSession(): SessionValue {
  const v = useContext(Ctx);
  if (!v) throw new Error("useSession outside SessionProvider");
  return v;
}
